// Pure coverage data + styling decisions for the market-coverage map.
//
// This module has NO DOM or Leaflet dependency so it can be unit-tested in
// isolation. Colours are plain hex strings; map.ts adapts them into Leaflet
// PathOptions.

export type CoverageType = 'power' | 'gas' | 'both' | 'none'

export type Tab = 'power' | 'gas'

export interface CountryStyle {
    highlighted: boolean
    color: string
    fillColor: string
    fillOpacity: number
    weight: number
}

const COVERAGE_TYPE: Record<string, CoverageType> = {
    'Norway': 'power', 'Sweden': 'power', 'Finland': 'power', 'Denmark': 'power',
    'United Kingdom': 'both', 'Ireland': 'power', 'Portugal': 'power', 'Spain': 'both',
    'Netherlands': 'both', 'Belgium': 'both', 'Luxembourg': 'none',
    'Germany': 'both', 'France': 'both', 'Switzerland': 'power', 'Austria': 'both',
    'Italy': 'both', 'Poland': 'power', 'Czech Republic': 'both', 'Slovakia': 'power',
    'Hungary': 'power', 'Romania': 'power', 'Bulgaria': 'power', 'Serbia': 'power',
    'Croatia': 'power', 'Slovenia': 'power', 'Greece': 'power',
}

// Highlight + greyed styles per commodity. The "both" coverage type renders in
// the active tab's highlight colour (blue on power, teal on gas); there is no
// dedicated purple fill any more.
const HIGHLIGHT_STYLE: Record<Tab, CountryStyle> = {
    power: { highlighted: true, color: '#1d4ed8', fillColor: '#3b82f6', fillOpacity: 0.65, weight: 1 },
    gas: { highlighted: true, color: '#0f766e', fillColor: '#14b8a6', fillOpacity: 0.65, weight: 1 },
}

const GREYED_STYLE: CountryStyle = {
    highlighted: false, color: '#94a3b8', fillColor: '#cbd5e1', fillOpacity: 0.4, weight: 0.5,
}

/**
 * A power bidding zone groups two or more whole countries that trade as a single
 * electricity market. Membership applies on the POWER tab only — on gas the
 * members stay independent. `feedCountry` names the member whose power feed
 * represents the zone in the joint tooltip.
 */
export interface PowerZone {
    id: string
    name: string
    members: string[]
    feedCountry: string
}

/**
 * Power bidding zones, expressed as data so future zones (e.g. Nordic NO1-5 or
 * the Italian zones tracked in #187) are a small, testable addition. Germany and
 * Luxembourg form the DE-LU zone, served by the DE Power feed.
 */
export const POWER_ZONES: readonly PowerZone[] = [
    { id: 'DE-LU', name: 'Germany & Luxembourg', members: ['Germany', 'Luxembourg'], feedCountry: 'Germany' },
]

/**
 * The power zone a country belongs to, or `null` if it belongs to none. Pure
 * lookup over `POWER_ZONES`.
 */
export function powerZoneFor(country: string): PowerZone | null {
    return POWER_ZONES.find((zone) => zone.members.includes(country)) ?? null
}

/**
 * The set of countries that should highlight together with `country` on the
 * given tab — always including the country itself. On the power tab a zone
 * member returns every member of its zone; on the gas tab (and for any non-zone
 * country) it returns only itself.
 */
export function zoneMembers(country: string, tab: Tab): string[] {
    if (tab === 'power') {
        const zone = powerZoneFor(country)
        if (zone) return [...zone.members]
    }

    return [country]
}

/**
 * Whether a country is covered for the given commodity tab. A 'both' country is
 * covered on both tabs; a 'power'/'gas' country only on its matching tab. A
 * power-zone member is additionally covered on the power tab even if its own
 * commodity is gas (e.g. Luxembourg belongs to the DE-LU power zone).
 */
export function isCovered(country: string, tab: Tab): boolean {
    const coverage = COVERAGE_TYPE[country] ?? 'none'

    if (coverage === tab || coverage === 'both') return true

    return tab === 'power' && powerZoneFor(country) !== null
}

/**
 * Resolve the style for a country on a given tab. Covered countries get the
 * commodity highlight colour; everything else is greyed.
 */
export function styleFor(country: string, tab: Tab): CountryStyle {
    return isCovered(country, tab) ? HIGHLIGHT_STYLE[tab] : GREYED_STYLE
}

/** The curve-product flags a feed exposes, as stored in country-meta.ts COUNTRY_META. */
export interface CurveTypeFlags {
    forward?: boolean
    fundamental?: boolean
    simulation?: boolean
}

/**
 * The curve-type labels shown in a coverage tooltip, in display order. KYOS
 * publishes no fundamental *gas* curves, so 'Fundamental curves' is suppressed
 * on the gas tab even when the feed's metadata flags it (the flag is set for the
 * country's power coverage). Power tooltips are unaffected.
 */
export function curveTypesFor(flags: CurveTypeFlags, tab: Tab): string[] {
    const types: string[] = []
    if (flags.forward) types.push('Forward curves')
    if (flags.fundamental && tab !== 'gas') types.push('Fundamental curves')
    if (flags.simulation) types.push('Simulation curves')
    return types
}

/** The two coverage-map views. Orthogonal to the commodity Tab, except for the
 *  one coupling rule encoded in resolveTabSelection. */
export type MapView = 'europe' | 'japan'

/**
 * Given the current view and the tab the user clicked, decide the resulting
 * view + active tab. Japan is power-only, so clicking Gas while on Japan
 * navigates back to Europe (where gas coverage lives) with Gas active. Every
 * other click keeps the current view and simply activates the clicked tab.
 */
export function resolveTabSelection(view: MapView, clickedTab: Tab): { view: MapView; tab: Tab } {
    if (view === 'japan' && clickedTab === 'gas') {
        return { view: 'europe', tab: 'gas' }
    }

    return { view, tab: clickedTab }
}

/**
 * Whether the Japan affordances should be visible on the given commodity tab.
 * Japan is power-only (MPX publishes fundamental power curves only), so on the
 * gas tab there is no Japan coverage to show: the corner-inset map switcher and
 * the "Via partner MPX (Japan)" legend row are both hidden. This mirrors the
 * AC "clicking Gas makes the Japan map invisible" and keeps Power and Gas as
 * autonomous maps (a power-only market simply isn't part of the gas map).
 */
export function isJapanVisible(tab: Tab): boolean {
    return tab === 'power'
}

export { COVERAGE_TYPE, HIGHLIGHT_STYLE }
